import { Component, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { DashboardStatsService } from '../../services/dashboard-stats.service';
import { Project } from '../../models/project.model';

type SortField = 'name' | 'dept' | 'scheme' | 'cost' | 'spent' | 'status' | 'delayedDate' | 'delayedBy';
type SortDir   = 'asc' | 'desc';

@Component({
    selector: 'app-delayed-projects',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './delayed-projects.component.html',
    styleUrls: ['./delayed-projects.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DelayedProjectsComponent {
    private stats  = inject(DashboardStatsService);
    private router = inject(Router);

    searchQuery = signal('');
    filterDept  = signal('');
    sortField   = signal<SortField>('delayedDate');
    sortDir     = signal<SortDir>('desc');
    currentPage = signal(1);
    readonly pageSize = 15;

    /** All OSD-flagged delayed projects (isDelayed=true only). */
    readonly allDelayed = computed(() => this.stats.explicitlyDelayedProjects());

    /** Unique department list for the filter dropdown. */
    readonly departments = computed(() =>
        [...new Set(this.allDelayed().map(p => p.dept).filter(Boolean))].sort()
    );

    readonly inProgressCount = computed(() =>
        this.allDelayed().filter(p => p.status === 'In Progress').length
    );
    readonly plannedCount = computed(() =>
        this.allDelayed().filter(p => p.status === 'Planned').length
    );

    readonly filtered = computed(() => {
        const q    = this.searchQuery().toLowerCase().trim();
        const dept = this.filterDept();
        let list   = this.allDelayed();

        if (dept) list = list.filter(p => p.dept === dept);
        if (q) {
            list = list.filter(p =>
                (p.name            || '').toLowerCase().includes(q) ||
                (p.dept            || '').toLowerCase().includes(q) ||
                (p.scheme          || '').toLowerCase().includes(q) ||
                ((p as any).gp     || '').toLowerCase().includes(q) ||
                ((p as any).block  || p.loc || '').toLowerCase().includes(q) ||
                ((p as any).delayedReason || '').toLowerCase().includes(q)
            );
        }

        const field = this.sortField();
        const dir   = this.sortDir();
        return [...list].sort((a, b) => {
            let av: any = this.sortValue(a, field);
            let bv: any = this.sortValue(b, field);
            if (typeof av === 'string') av = av.toLowerCase();
            if (typeof bv === 'string') bv = bv.toLowerCase();
            if (av < bv) return dir === 'asc' ? -1 : 1;
            if (av > bv) return dir === 'asc' ?  1 : -1;
            return 0;
        });
    });

    readonly totalPages = computed(() => Math.max(1, Math.ceil(this.filtered().length / this.pageSize)));

    readonly paged = computed(() => {
        const start = (this.currentPage() - 1) * this.pageSize;
        return this.filtered().slice(start, start + this.pageSize);
    });

    private sortValue(p: Project, field: SortField): any {
        switch (field) {
            case 'name':        return p.name         ?? '';
            case 'dept':        return p.dept         ?? '';
            case 'scheme':      return p.scheme       ?? '';
            case 'cost':        return p.cost         ?? 0;
            case 'spent':       return p.spent        ?? 0;
            case 'status':      return p.status       ?? '';
            case 'delayedDate': return (p as any).delayedDate ?? (p as any).updatedAt ?? '';
            case 'delayedBy':   return (p as any).delayedBy  ?? '';
            default:            return '';
        }
    }

    setSort(field: SortField): void {
        if (this.sortField() === field) {
            this.sortDir.update(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            this.sortField.set(field);
            this.sortDir.set('asc');
        }
        this.currentPage.set(1);
    }

    sortIcon(field: SortField): string {
        if (this.sortField() !== field) return 'fa-solid fa-sort';
        return this.sortDir() === 'asc' ? 'fa-solid fa-sort-up' : 'fa-solid fa-sort-down';
    }

    onSearch(value: string): void {
        this.searchQuery.set(value);
        this.currentPage.set(1);
    }

    onDeptFilter(value: string): void {
        this.filterDept.set(value);
        this.currentPage.set(1);
    }

    openProject(id: string): void {
        this.router.navigate(['/dhamnagar-dashboard/project', id]);
    }

    goBack(): void {
        this.router.navigate(['/dhamnagar-dashboard/dashboard']);
    }

    prevPage(): void { if (this.currentPage() > 1) this.currentPage.update(p => p - 1); }
    nextPage(): void { if (this.currentPage() < this.totalPages()) this.currentPage.update(p => p + 1); }

    formatDate(d: any): string {
        if (!d) return '—';
        if (typeof d === 'string' && d.length >= 10) {
            const parts = d.split('-');
            if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
            return d;
        }
        if (d?.toDate) return d.toDate().toLocaleDateString('en-IN');
        return '—';
    }

    formatCost(v: number | undefined): string {
        if (!v) return '—';
        return '₹' + (v).toLocaleString('en-IN') + 'L';
    }
}
